import { useEffect, useRef, useState, type CSSProperties, type ReactNode } from 'react';
import { Link } from 'react-router';
import { useLignerPaa, useVarsleMeg, type MemoryCard } from '../api/useHypoteseHukommelse.ts';
import type { HypothesisConfig } from '../api/useHypotheses.ts';
import { Ikon } from '../status/Ikon.tsx';
import { hypoteseOppforing, hypoteseStatusNoekkel, tokenVar } from '../status/status.ts';
import { Tekstknapp } from './deler.tsx';
import { grunnTekst, kanFoelges, kortDatoTekst, lignerTittel } from './kilder.ts';
import { nummerTekst } from './Kvittering.tsx';
import { statusNavn } from './lesning.ts';
import type { Skjema } from './skjema.ts';

/**
 * «Ligner på N som er vurdert før» (H3.3, konseptsiden W2–W4, M1; H12): inntil tre hypoteser som
 * er vurdert før, ved siden av skjemaet mens innsenderen skriver. Et tilbud, aldri en sperre: uten
 * treff — eller når søket feiler eller bruker mer enn ett sekund — står ingenting, og `tom` (tipset)
 * står der kortene ville stått (W1). Ingen likhetsprosent og ingen rangering.
 *
 * Valgene står i kortet: «Min er en annen» (ett felt, `supersedesId` + `differenceNote`) på alle,
 * «Varsle meg når den vurderes igjen» på en åpen, «Se konklusjonen» på en konkludert. Pekeren er
 * én, så «Min er en annen» forsvinner fra de andre mens ett kort er valgt (avvik 2).
 */
export function LignerPaa({
  id,
  runde,
  aktiv,
  skjema,
  config,
  smal,
  onLagre,
  tom = null,
  tittelTag: TittelTag = 'h3',
}: {
  id: string | null;
  /** Leddet som står åpent, eller «les-over»: søket kjøres på nytt når personen går videre. */
  runde: string;
  aktiv: boolean;
  skjema: Skjema;
  config: HypothesisConfig | undefined;
  smal: boolean;
  /** Skriver «Min er en annen» i utkastet og lagrer straks. */
  onLagre: (patch: Pick<Skjema, 'supersedesId' | 'differenceNote'>) => void;
  tom?: ReactNode;
  tittelTag?: 'h2' | 'h3';
}) {
  const treff = useLignerPaa(id, runde, aktiv);
  const varsle = useVarsleMeg();
  const [redigerer, setRedigerer] = useState<string | null>(null);
  const [tekst, setTekst] = useState('');
  const feltRef = useRef<HTMLTextAreaElement>(null);
  useEffect(() => {
    if (redigerer) feltRef.current?.focus();
  }, [redigerer]);

  const kort = treff.isSuccess ? treff.data : [];
  if (!aktiv || kort.length === 0) return <>{tom}</>;

  // Kortet som er valgt: det som redigeres, eller det utkastet peker på.
  const valgtId = redigerer ?? skjema.supersedesId ?? null;
  const aapne = (k: MemoryCard) => {
    setTekst(skjema.supersedesId === k.hypothesis.id ? skjema.differenceNote : '');
    setRedigerer(k.hypothesis.id);
  };
  const lagre = () => {
    const t = tekst.trim();
    onLagre(t ? { supersedesId: redigerer ?? undefined, differenceNote: tekst } : { supersedesId: undefined, differenceNote: '' });
    setRedigerer(null);
  };

  return (
    <section className="h-lp" aria-labelledby="h-lp-h" data-testid="h-ligner">
      <TittelTag id="h-lp-h" className="h-lp-overskrift">
        {lignerTittel(kort.length)}
      </TittelTag>
      {kort.map((k) => {
        const nr = k.hypothesis.number;
        const o = hypoteseOppforing(hypoteseStatusNoekkel(k.status, k.outcome));
        const grunn = grunnTekst(k, config);
        const erValgt = valgtId === k.hypothesis.id;
        const lagret = !redigerer && skjema.supersedesId === k.hypothesis.id && skjema.differenceNote.trim() !== '';
        const kanSiAnnen = valgtId === null;
        const foelg = kanFoelges(k.statusType, k.status);
        return (
          <article key={k.hypothesis.id} className={erValgt ? 'h-lp-kort h-lp-kort-valgt' : 'h-lp-kort'} aria-labelledby={`h-lp-t-${nr}`} data-testid={`h-lp-${nr}`}>
            <div className="h-lp-l1">
              <span className="h-lp-status" style={{ '--hb-status': tokenVar(o.fg) } as CSSProperties}>
                <Ikon navn={o.ikon} />
                {statusNavn(config, k)}
              </span>
              <span>{nummerTekst(nr)}</span>
              <span>{kortDatoTekst(k)}</span>
            </div>
            <p id={`h-lp-t-${nr}`} className="h-lp-tittel">
              {k.hypothesis.title}
            </p>
            {grunn ? <p className="h-lp-grunn">{grunn}</p> : null}
            {redigerer === k.hypothesis.id ? (
              <div className="h-lp-felt">
                <label htmlFor={`h-lp-f-${nr}`} className="h-etikett">
                  Hva er annerledes med din?
                </label>
                <textarea ref={feltRef} id={`h-lp-f-${nr}`} className="h-input h-tekstfelt" rows={smal ? 5 : 4} maxLength={280} value={tekst} onChange={(e) => setTekst(e.target.value)} data-testid="h-differenceNote" />
                <div className="h-lp-handlinger">
                  <button type="button" className="panel-knapp h-lp-lagre" onClick={lagre}>
                    Lagre
                  </button>
                  <Tekstknapp onClick={() => setRedigerer(null)}>Avbryt</Tekstknapp>
                </div>
              </div>
            ) : lagret ? (
              <p className="h-lp-svart">
                <Ikon navn="checkmark-outline" />
                <span>Du har skrevet hva som er annerledes.</span>
                <Tekstknapp onClick={() => aapne(k)}>Endre</Tekstknapp>
              </p>
            ) : null}
            {k.watching ? (
              <p className="h-lp-svart">
                <Ikon navn="notifications-outline" />
                <span>Du får den i Til deg når noen tar den opp igjen.</span>
                <button type="button" className="panel-knapp-lenke h-noeytral" onClick={() => varsle.mutate({ id: k.hypothesis.id, paa: false })}>
                  Slutt å varsle
                </button>
              </p>
            ) : null}
            {(kanSiAnnen || (foelg && !k.watching) || k.status === 'konkludert') && redigerer !== k.hypothesis.id ? (
              <div className="h-lp-handlinger">
                {kanSiAnnen ? <Tekstknapp onClick={() => aapne(k)}>Min er en annen</Tekstknapp> : null}
                {foelg && !k.watching ? (
                  <button type="button" className="panel-knapp-lenke" aria-label="Varsle meg når den vurderes igjen" aria-busy={varsle.isPending || undefined} onClick={() => varsle.mutate({ id: k.hypothesis.id, paa: true })}>
                    <Ikon navn="notifications-outline" />
                    {smal ? 'Varsle meg' : 'Varsle meg når den vurderes igjen'}
                  </button>
                ) : null}
                {k.status === 'konkludert' ? (
                  <Link to={`/h/registeret/${nr}`} className="panel-knapp-lenke h-noeytral">
                    Se konklusjonen
                  </Link>
                ) : null}
              </div>
            ) : null}
          </article>
        );
      })}
    </section>
  );
}
